import React, { useEffect, useState } from 'react';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { useTranslation } from 'react-i18next';
import './ExperiencesMendoza.css';

const ExperiencesMendoza: React.FC = () => {
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, []);

  // Experience items data with translated content
  const experiences = [
    {
      id: 1,
      image: '/assets/experience-guide-1.png',
      title: t('properties/mendoza:experiences.activities.cooking.title'),
      location: t('properties/mendoza:experiences.activities.cooking.location'),
      description: t('properties/mendoza:experiences.activities.cooking.description')
    },
    {
      id: 2,
      image: '/assets/experience-guide-2.png',
      title: t('properties/mendoza:experiences.activities.horseback.title'),
      location: t('properties/mendoza:experiences.activities.horseback.location'),
      description: t('properties/mendoza:experiences.activities.horseback.description')
    },
    {
      id: 3,
      image: '/assets/experience-guide-3.png',
      title: t('properties/mendoza:experiences.activities.wineTour.title'),
      location: t('properties/mendoza:experiences.activities.wineTour.location'),
      description: t('properties/mendoza:experiences.activities.wineTour.description')
    },
    {
      id: 4,
      image: '/assets/experience-guide-4.png',
      title: t('properties/mendoza:experiences.activities.hiking.title'),
      location: t('properties/mendoza:experiences.activities.hiking.location'),
      description: t('properties/mendoza:experiences.activities.hiking.description')
    },
    {
      id: 5,
      image: '/assets/experience-guide-5.png',
      title: t('properties/mendoza:experiences.activities.ranch.title'),
      location: t('properties/mendoza:experiences.activities.ranch.location'),
      description: t('properties/mendoza:experiences.activities.ranch.description')
    },
    {
      id: 6,
      image: '/assets/experience-guide-6.png',
      title: t('properties/mendoza:experiences.activities.aconcagua.title'),
      location: t('properties/mendoza:experiences.activities.aconcagua.location'),
      description: t('properties/mendoza:experiences.activities.aconcagua.description')
    }
  ];

  // Team members data with translated content
  const teamMembers = [
    {
      id: 1,
      image: '/assets/images/localhost/9fe76396d22e63fa2f0e5f856d1c5e01988dbbb6.png',
      name: t('properties/mendoza:experiences.team.members.carlosBarros.name'),
      role: t('properties/mendoza:experiences.team.members.carlosBarros.role'),
      description: t('properties/mendoza:experiences.team.members.carlosBarros.description')
    },
    {
      id: 2,
      image: '/assets/images/localhost/b23b62a9f96cc5ab8a93da33fe544df6c49884a3.png',
      name: t('properties/mendoza:experiences.team.members.cristianLopez.name'),
      role: t('properties/mendoza:experiences.team.members.cristianLopez.role'),
      description: t('properties/mendoza:experiences.team.members.cristianLopez.description')
    },
    {
      id: 3,
      image: '/assets/images/localhost/0900d5204eb6e5804f3a3f031befd03ac9cff02f.png',
      name: t('properties/mendoza:experiences.team.members.paulaBertotto.name'),
      role: t('properties/mendoza:experiences.team.members.paulaBertotto.role'),
      description: t('properties/mendoza:experiences.team.members.paulaBertotto.description')
    }
  ];

  return (
    <div className="experiences-mendoza">
      {/* Navigation - Light variant for Mendoza */}
      <UnifiedNavigation variant="light" destination="mendoza" />

      {/* Text Block Header */}
      <section className="experiences-header-section" data-scroll-section>
        <div className="experiences-header-content">
          <h1>{t('properties/mendoza:experiences.pageTitle')}</h1>
          <p>
            {t('properties/mendoza:experiences.pageDescription')}
          </p>
        </div>
      </section>

      {/* Full Width Hero Image */}
      <section className="experiences-hero-image" data-scroll-section>
        <div 
          className="hero-image-container"
          style={{ backgroundImage: `url('/assets/experiencepagebreak.jpg')` }}
        />
      </section>

      {/* Guide/Experiences Section - 3x2 Grid */}
      <section className="experiences-guide-section" data-scroll-section>
        <div className="guide-section-container">
          {/* Filters and Guide Button */}
          <div className="guide-section-header">
            <div className="filter-buttons">
              <HandDrawnButton
                text={t('properties/mendoza:experiences.filters.location')}
                onClick={() => console.log('Mendoza filter')}
                variant="mendoza"
                color="awasi"
                animation="stroke-to-fill"
                size="normal"
                className="experiences-filter-button experiences-filter-active"
              />
              <HandDrawnButton
                text={t('properties/mendoza:experiences.filters.type')}
                onClick={() => console.log('Type filter')}
                variant="mendoza"
                color="awasi"
                animation="stroke-to-stroke"
                size="normal"
                className="experiences-filter-button"
              />
            </div>
            <HandDrawnButton
              text={t('properties/mendoza:experiences.filters.ourGuides')}
              onClick={() => console.log('Our guides')}
              variant="mendoza"
              color="awasi"
              animation="stroke-to-stroke"
              size="normal"
              className="experiences-guide-button"
            />
          </div>

          <div className="experiences-grid">
            {experiences.map((experience) => (
              <div key={experience.id} className="experience-card">
                <div className="experience-image">
                  <img src={experience.image} alt={experience.title} />
                </div>
                <div className="experience-content">
                  <h3>{experience.title}</h3>
                  <span className="experience-location">{experience.location}</span>
                  <p>{experience.description}</p>
                  <HandDrawnButton
                    text={t('properties/mendoza:experiences.contactUs')}
                    onClick={() => console.log('Contact us')}
                    variant="mendoza"
                    color="awasi"
                    animation="stroke-to-fill"
                    size="normal"
                    className="experience-contact-button"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="guide-pagination">
            {[1, 2, 3, 4].map(pageNum => (
              <HandDrawnButton
                key={pageNum}
                text={pageNum.toString()}
                onClick={() => setCurrentPage(pageNum)}
                variant="mendoza"
                color="awasi"
                animation={currentPage === pageNum ? "stroke-to-fill" : "stroke-to-stroke"}
                size="normal"
                className={`experiences-page-button ${currentPage === pageNum ? 'experiences-page-active' : ''}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Team Header + Body */}
      <section className="experiences-team-section" data-scroll-section>
        <div className="team-section-container">
          <div className="team-header">
            <h2>{t('properties/mendoza:experiences.team.sectionTitle')}</h2>
            <p>
              {t('properties/mendoza:experiences.team.sectionDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Carousel */}
      <section className="experiences-team-carousel" data-scroll-section>
        <div className="team-carousel-wrapper">
          <div className="team-carousel-container" id="team-carousel">
            <div className="team-members-scroll">
              {teamMembers.map((member) => (
                <div key={member.id} className="team-member-card">
                  <div className="member-image">
                    {member.image ? (
                      <img src={member.image} alt={member.name} />
                    ) : (
                      <div className="placeholder-image" />
                    )}
                  </div>
                  <div className="member-content">
                    <h3>{member.name}</h3>
                    <span className="member-role">{member.role}</span>
                    <p>{member.description}</p>
                    <button className="read-more-link">
                      {t('properties/mendoza:experiences.team.readMore')}
                      <svg className="underline" viewBox="0 0 76 2" fill="none">
                        <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-nav-arrow" aria-label="Next">
              <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                <path d="M22 14L37 29L22 44" stroke="#49413d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Full Image */}
      <section className="experiences-cta-section" data-scroll-section>
        <div 
          className="cta-image-container"
          style={{ backgroundImage: `url('/assets/fullviewportatacama.jpg')` }}
        />
      </section>

      {/* Instagram Gallery */}
      <div className="instagram-section-wrapper">
        <InstagramSection />
      </div>

      {/* Footer - Mendoza variant */}
      <Footer variant="mendoza" />
    </div>
  );
};

export default ExperiencesMendoza;